(function () {
  'use strict';

  var activeLookupId = 0;
  var cachedMatches = {};
  var renderLock = false;
  var lastExactQuery = '';
  var lastExactMatch = null;
  var lastExactState = '';
  var lastExactMessage = '';

  function normalizeSku(value) {
    return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function escapeHtml(value) {
    return (value || '')
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function looksLikeSku(value) {
    var query = normalizeSku(value);
    var parts;
    var compact;

    if (query.length < 5) return false;
    if (!/[0-9]/.test(query)) return false;
    if (!/[a-z]/i.test(query)) return false;
    if (!/^[a-z0-9._\-\s]+$/i.test(query)) return false;

    parts = query.split(/[\s._\-]+/).filter(Boolean);
    compact = query.replace(/[\s._\-]+/g, '');

    if (parts.length > 1) {
      return parts.some(function (part) {
        return part.length >= 4 && /[a-z]/i.test(part) && /[0-9]/.test(part);
      });
    }

    return /^[a-z]{2,}\d{2,}[a-z0-9]*$/i.test(compact) || /^\d{2,}[a-z]{2,}[a-z0-9]*$/i.test(compact);
  }

  function getShopifyRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return '/';
  }

  function getSearchQueryFromForm(form) {
    if (!form) return '';

    var visibleInput = form.querySelector('[data-input-search]:not([type="hidden"])');
    var namedInput = form.querySelector('input[name="q"]');

    if (visibleInput && visibleInput.value) return visibleInput.value;
    if (namedInput && namedInput.value) return namedInput.value;

    return '';
  }

  function getInputFromEventTarget(target) {
    if (!target) return null;

    if (target.matches && target.matches('[data-input-search], input[name="q"]')) {
      return target;
    }

    return null;
  }

  function getActiveSearchInput() {
    var active = document.activeElement;

    if (active && active.matches && active.matches('[data-input-search], input[name="q"]')) {
      return active;
    }

    return document.querySelector('[data-input-search]:focus, input[name="q"]:focus') || document.querySelector('[data-input-search], input[name="q"]');
  }

  function buildSuggestUrl(query) {
    var cleanQuery = query.trim().replace(/"/g, '');
    var url = new URL(getShopifyRoot() + 'search/suggest.json', window.location.origin);

    url.searchParams.set('q', '"' + cleanQuery + '"');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', 'variants.sku');

    return url.toString();
  }

  function getProductHandleFromUrl(urlValue) {
    var url;
    var path;
    var pieces;

    if (!urlValue) return '';

    try {
      url = new URL(urlValue, window.location.origin);
      path = url.pathname;
    } catch (error) {
      path = urlValue;
    }

    if (path.indexOf('/products/') === -1) return '';

    pieces = path.split('/products/')[1].split('/')[0].split('?')[0];
    return pieces || '';
  }

  function buildProductJsonUrl(productUrl) {
    var url;
    var path;
    var prefix;
    var handle = getProductHandleFromUrl(productUrl);

    if (!handle) return '';

    try {
      url = new URL(productUrl, window.location.origin);
      path = url.pathname;
      prefix = path.split('/products/')[0] || '';
      return prefix + '/products/' + handle + '.js';
    } catch (error) {
      return getShopifyRoot() + 'products/' + handle + '.js';
    }
  }

  function getProductUrl(match) {
    var product = match && match.product ? match.product : null;
    var variant = match && match.variant ? match.variant : null;
    var candidateUrl = match && match.candidate && match.candidate.url ? match.candidate.url : '';
    var baseUrl;

    if (!product) return '';

    baseUrl = candidateUrl || product.url || (getShopifyRoot() + 'products/' + product.handle);

    if (variant && variant.id && baseUrl.indexOf('variant=') === -1) {
      return baseUrl.split('?')[0] + '?variant=' + variant.id;
    }

    return baseUrl;
  }

  function fetchProductJson(candidate) {
    var productJsonUrl = buildProductJsonUrl(candidate && candidate.url);

    if (!productJsonUrl) return Promise.resolve(null);

    return fetch(productJsonUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (product) {
        if (!product) return null;
        return {
          candidate: candidate,
          product: product
        };
      })
      .catch(function () {
        return null;
      });
  }

  function findExactVariant(product, normalizedQuery) {
    var variants = product && product.variants ? product.variants : [];
    var i;

    for (i = 0; i < variants.length; i += 1) {
      if (normalizeSku(variants[i].sku) === normalizedQuery) {
        return variants[i];
      }
    }

    return null;
  }

  function findExactSkuProduct(query) {
    var normalizedQuery = normalizeSku(query);
    var cacheKey = normalizedQuery;

    if (!looksLikeSku(normalizedQuery)) {
      return Promise.resolve(null);
    }

    if (Object.prototype.hasOwnProperty.call(cachedMatches, cacheKey)) {
      return Promise.resolve(cachedMatches[cacheKey]);
    }

    return fetch(buildSuggestUrl(query), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Exact SKU lookup failed');
        return response.json();
      })
      .then(function (data) {
        var results = data && data.resources && data.resources.results ? data.resources.results : {};
        var products = results.products || [];

        return Promise.all(products.map(fetchProductJson));
      })
      .then(function (items) {
        var i;
        var variant;
        var item;

        for (i = 0; i < items.length; i += 1) {
          item = items[i];
          if (!item || !item.product) continue;

          variant = findExactVariant(item.product, normalizedQuery);

          if (variant) {
            cachedMatches[cacheKey] = {
              candidate: item.candidate,
              product: item.product,
              variant: variant
            };
            return cachedMatches[cacheKey];
          }
        }

        cachedMatches[cacheKey] = null;
        return null;
      })
      .catch(function () {
        cachedMatches[cacheKey] = null;
        return null;
      });
  }

  function getSearchScope(formOrInput) {
    var node = formOrInput;

    if (!node) return document;

    return (
      node.closest('.t4s-drawer') ||
      node.closest('[data-predictive-search]') ||
      node.closest('.t4s-search-header__form-wrap') ||
      node.closest('.t4s-search-page') ||
      document
    );
  }

  function getSearchContainers(scope) {
    var containers = [];

    if (!scope || !scope.querySelectorAll) scope = document;

    containers = Array.prototype.slice.call(scope.querySelectorAll('[data-results-search]'));

    if (!containers.length && scope !== document) {
      containers = Array.prototype.slice.call(document.querySelectorAll('[data-results-search]'));
    }

    return containers;
  }

  function getTitleNodes(scope) {
    if (!scope || !scope.querySelectorAll) scope = document;
    return Array.prototype.slice.call(scope.querySelectorAll('[data-title-search]'));
  }

  function getViewAllNodes(scope) {
    if (!scope || !scope.querySelectorAll) scope = document;
    return Array.prototype.slice.call(scope.querySelectorAll('[data-viewAll-search]'));
  }

  function ensureStyles() {
    if (document.getElementById('exact-sku-search-style')) return;

    var style = document.createElement('style');
    style.id = 'exact-sku-search-style';
    style.textContent = [
      '.t4s-exact-sku-active{display:flex!important;justify-content:center!important;align-items:flex-start!important;flex-wrap:wrap!important;}',
      '.t4s-exact-sku-active .t4s-exact-sku-result{max-width:220px;width:100%;}',
      '.t4s-exact-sku-message{width:100%;padding:28px 16px;text-align:center;font-size:14px;}',
      '.t4s-exact-sku-result .t4s-widget_img_pr img{max-height:180px;object-fit:contain;}'
    ].join('');

    document.head.appendChild(style);
  }

  function beginRenderLock() {
    renderLock = true;

    window.setTimeout(function () {
      renderLock = false;
    }, 60);
  }

  function getProductImage(match) {
    var product = match && match.product ? match.product : {};
    var variant = match && match.variant ? match.variant : null;
    var candidate = match && match.candidate ? match.candidate : {};

    if (variant && variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant && variant.featured_image) return variant.featured_image;
    if (product.featured_image) return product.featured_image;
    if (product.images && product.images.length) return product.images[0];
    if (candidate.featured_image && candidate.featured_image.url) return candidate.featured_image.url;
    if (candidate.image) return candidate.image;

    return '';
  }

  function formatPrice(cents) {
    var currency = 'ARS';
    var amount;

    if (cents === null || typeof cents === 'undefined' || cents === '') return '';

    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      currency = window.Shopify.currency.active;
    }

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (error) {}
    }

    amount = Number(cents) / 100;

    if (Number.isNaN(amount)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-AR', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (error) {
      return '$' + amount.toLocaleString();
    }
  }

  function buildExactProductCard(match) {
    var product = match && match.product ? match.product : {};
    var variant = match && match.variant ? match.variant : null;
    var title = product.title || (match.candidate && match.candidate.title) || '';
    var image = getProductImage(match);
    var url = getProductUrl(match);
    var price = variant && typeof variant.price !== 'undefined' ? variant.price : product.price;
    var priceHtml = formatPrice(price);
    var sku = variant && variant.sku ? variant.sku : '';

    return [
      '<div class="t4s-col-item t4s-space-item-inner t4s-widget__pr t4s-exact-sku-result">',
        '<div class="t4s-widget_img_pr">',
          '<a class="t4s-d-block t4s-pr t4s-oh" href="' + escapeHtml(url) + '">',
            image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(title) + '" loading="lazy">' : '',
          '</a>',
        '</div>',
        '<div class="t4s-widget_if_pr">',
          '<a href="' + escapeHtml(url) + '" class="t4s-d-block t4s-widget__pr-title">' + escapeHtml(title) + '</a>',
          sku ? '<div class="t4s-exact-sku-code">' + escapeHtml(sku) + '</div>' : '',
          priceHtml ? '<div class="t4s-widget__pr-price">' + escapeHtml(priceHtml) + '</div>' : '',
        '</div>',
      '</div>'
    ].join('');
  }

  function renderMessage(scope, message) {
    ensureStyles();
    beginRenderLock();

    getSearchContainers(scope).forEach(function (container) {
      container.classList.add('t4s-exact-sku-active');
      container.innerHTML = '<div class="t4s-exact-sku-message">' + escapeHtml(message) + '</div>';
      container.style.display = '';
    });

    getTitleNodes(scope).forEach(function (node) {
      node.textContent = 'Búsqueda por SKU';
      node.style.display = '';
    });

    getViewAllNodes(scope).forEach(function (node) {
      node.style.display = 'none';
    });
  }

  function renderExactMatch(scope, match) {
    var html = buildExactProductCard(match);

    ensureStyles();
    beginRenderLock();

    getSearchContainers(scope).forEach(function (container) {
      container.classList.add('t4s-exact-sku-active');
      container.innerHTML = html;
      container.style.display = '';
    });

    getTitleNodes(scope).forEach(function (node) {
      node.textContent = 'Resultado exacto por SKU';
      node.style.display = '';
    });

    getViewAllNodes(scope).forEach(function (node) {
      node.style.display = 'none';
    });
  }

  function releaseExactMode(scope) {
    if (!scope || !scope.querySelectorAll) scope = document;

    lastExactQuery = '';
    lastExactMatch = null;
    lastExactState = '';
    lastExactMessage = '';

    getSearchContainers(scope).forEach(function (container) {
      container.classList.remove('t4s-exact-sku-active');
    });

    getViewAllNodes(scope).forEach(function (node) {
      node.style.display = '';
    });
  }

  function setExactState(state, query, scope, match, message) {
    lastExactQuery = normalizeSku(query);
    lastExactMatch = match || null;
    lastExactState = state || '';
    lastExactMessage = message || '';

    if (state === 'match' && match) {
      renderExactMatch(scope, match);
      return;
    }

    if (message) {
      renderMessage(scope, message);
    }
  }

  function handlePredictiveSku(input) {
    var query = input && input.value ? input.value : '';
    var scope = getSearchScope(input);
    var lookupId;

    if (!looksLikeSku(query)) {
      releaseExactMode(scope);
      return;
    }

    lookupId = ++activeLookupId;

    setExactState('loading', query, scope, null, 'Buscando SKU exacto...');

    findExactSkuProduct(query).then(function (match) {
      if (lookupId !== activeLookupId) return;

      if (match) {
        setExactState('match', query, scope, match, '');
      } else {
        setExactState('not_found', query, scope, null, 'No se encontró un producto con ese SKU exacto.');
      }
    });
  }

  function debounce(fn, wait) {
    var timeout;

    return function () {
      var args = arguments;
      var context = this;

      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  var debouncedPredictiveSku = debounce(function (input) {
    handlePredictiveSku(input);
  }, 180);

  function redirectToMatch(match) {
    var url = getProductUrl(match);

    if (!url) return false;

    window.location.assign(url);
    return true;
  }

  function runExactSkuSearch(query, fallback, scope) {
    if (!looksLikeSku(query)) {
      if (typeof fallback === 'function') fallback();
      return;
    }

    setExactState('loading', query, scope || document, null, 'Buscando SKU exacto...');

    findExactSkuProduct(query)
      .then(function (match) {
        if (match) {
          redirectToMatch(match);
        } else {
          setExactState('not_found', query, scope || document, null, 'No se encontró un producto con ese SKU exacto.');
        }
      })
      .catch(function () {
        setExactState('not_found', query, scope || document, null, 'No se encontró un producto con ese SKU exacto.');
      });
  }

  function reapplyExactStateIfNeeded() {
    var activeInput;
    var scope;

    if (renderLock || !lastExactQuery || !lastExactState) return;

    activeInput = getActiveSearchInput();

    if (!activeInput || normalizeSku(activeInput.value) !== lastExactQuery) return;

    scope = getSearchScope(activeInput);

    if (lastExactState === 'match' && lastExactMatch) {
      renderExactMatch(scope, lastExactMatch);
      return;
    }

    if ((lastExactState === 'loading' || lastExactState === 'not_found') && lastExactMessage) {
      renderMessage(scope, lastExactMessage);
    }
  }

  document.addEventListener(
    'input',
    function (event) {
      var input = getInputFromEventTarget(event.target);

      if (!input) return;

      debouncedPredictiveSku(input);
    },
    true
  );

  document.addEventListener(
    'keyup',
    function (event) {
      var input = getInputFromEventTarget(event.target);

      if (!input) return;

      debouncedPredictiveSku(input);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target && event.target.closest ? event.target.closest('form[action*="/search"]') : null;
      var query;
      var scope;

      if (!form) return;

      query = getSearchQueryFromForm(form);

      if (!looksLikeSku(query)) return;

      event.preventDefault();
      event.stopPropagation();

      scope = getSearchScope(form);
      runExactSkuSearch(query, null, scope);
    },
    true
  );

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('[data-viewAll-search], a[href*="/search"], button[type="submit"]') : null;
    var input;
    var form;
    var query;
    var scope;

    if (!button) return;

    input = getActiveSearchInput();
    form = button.closest('form[action*="/search"]') || (input ? input.closest('form[action*="/search"]') : null);
    query = form ? getSearchQueryFromForm(form) : input ? input.value : '';

    if (!looksLikeSku(query)) return;

    event.preventDefault();
    event.stopPropagation();

    scope = getSearchScope(form || input);
    runExactSkuSearch(query, null, scope);
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    var searchParams;
    var query;

    if (window.location.pathname.indexOf('/search') !== -1) {
      searchParams = new URLSearchParams(window.location.search);
      query = searchParams.get('q') || '';

      if (looksLikeSku(query)) {
        runExactSkuSearch(query, null, document);
      }
    }

    if (window.MutationObserver) {
      new MutationObserver(function () {
        window.setTimeout(reapplyExactStateIfNeeded, 0);
      }).observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    window.setInterval(reapplyExactStateIfNeeded, 500);
  });
})();
